import { ReactNode, useState, useEffect } from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useDeveloperShortcuts } from '@/hooks/useDeveloperShortcuts';

interface QuickAction {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  shortcut?: string;
}

interface DeveloperPageLayoutProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  quickActions?: QuickAction[];
  showRefreshControl?: boolean;
  refreshInterval?: number;
  children: ReactNode;
}

const formatRelativeTime = (date: Date) => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  
  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  
  const diffHours = Math.floor(diffMinutes / 60);
  return `${diffHours}h ago`;
};

export function DeveloperPageLayout({ 
  title, 
  subtitle,
  actions,
  quickActions,
  showRefreshControl = true,
  refreshInterval = 10000,
  children 
}: DeveloperPageLayoutProps) {
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Initialize developer shortcuts
  const customShortcuts = quickActions?.map(action => ({
    key: action.shortcut || '',
    action: action.onClick,
    description: action.label
  })).filter(s => s.key) || [];
  
  useDeveloperShortcuts(customShortcuts);
  
  // Auto-refresh logic
  useEffect(() => {
    if (!isAutoRefresh) return;
    
    const interval = setInterval(() => {
      setLastRefresh(new Date());
      // Trigger refresh de datos - this would emit an event or call refresh handlers
      window.dispatchEvent(new CustomEvent('developer-refresh'));
    }, refreshInterval);
    
    return () => clearInterval(interval);
  }, [isAutoRefresh, refreshInterval]);
  
  return (
    <div className="space-y-4">
      {/* Header técnico con controles */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              {title}
              {showRefreshControl && (
                <Badge variant="outline" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatRelativeTime(lastRefresh)}
                </Badge>
              )}
            </h1>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Quick actions */}
            {quickActions?.map((action, idx) => (
              <TooltipProvider key={idx}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="sm"
                      variant={action.variant === 'danger' ? 'destructive' : 'outline'}
                      onClick={action.onClick}
                    >
                      {action.icon}
                      <span className="ml-2 hidden lg:inline">{action.label}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{action.label}</p>
                    {action.shortcut && (
                      <p className="text-xs opacity-70">{action.shortcut}</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
            
            {/* Refresh controls */}
            {showRefreshControl && (
              <>
                <Separator orientation="vertical" className="h-6" />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLastRefresh(new Date())}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Switch
                  checked={isAutoRefresh}
                  onCheckedChange={setIsAutoRefresh}
                />
              </>
            )}
            
            {/* Additional actions */}
            {actions}
          </div>
        </div>
      </div>
      
      {/* Content area */}
      <div className="space-y-4">{children}</div>
    </div>
  );
}